import React, { Fragment, useMemo, useState } from 'react';
import { SearchXIcon } from 'lucide-react';
import { slots } from '../../data/slots';
import type { Slot, Volatility } from '../../types/slot';
import { useSiteSettings } from '../../contexts/SiteSettingsContext';
import { SlotRow, rowGrid } from './SlotRow';
import { SlotCard } from './SlotCard';
import { CasinoInlineOffer } from './CasinoInlineOffer';
import { trackEvent } from '../../utils/analytics';
type SortKey = 'rank' | 'rtp' | 'new' | 'maxwin';
const providerFilters = ['Alle', 'Pragmatic Play', "Play'n GO", 'NetEnt', 'Hacksaw Gaming', 'Overige'];
const mainProviders = providerFilters.slice(1, -1);
const volatilityFilters: ('Alle' | Volatility)[] = ['Alle', 'Laag', 'Gemiddeld', 'Hoog', 'Zeer hoog'];
function parseMaxWin(v: string): number {
  const n = Number(v.replace(/[^\d]/g, ''));
  return Number.isNaN(n) || n === 0 ? Infinity : n;
}
export function RankingSection({
  onDemo
}: {
  onDemo: (s: Slot) => void;
}) {
  const {
    rankingLayout,
    showRealMoneyCtas
  } = useSiteSettings();
  const [provider, setProvider] = useState('Alle');
  const [volatility, setVolatility] = useState<'Alle' | Volatility>('Alle');
  const [sort, setSort] = useState<SortKey>('rank');
  const visible = useMemo(() => {
    const list = slots.filter(s => {
      const p = provider === 'Alle' || (provider === 'Overige' ? !mainProviders.includes(s.provider) : s.provider === provider);
      const v = volatility === 'Alle' || s.volatility === volatility;
      return p && v;
    });
    const sorted = [...list];
    if (sort === 'rtp') sorted.sort((a, b) => b.rtp - a.rtp);
    if (sort === 'new') sorted.sort((a, b) => b.release - a.release || a.rank - b.rank);
    if (sort === 'maxwin') sorted.sort((a, b) => parseMaxWin(b.maxWin) - parseMaxWin(a.maxWin));
    return sorted;
  }, [provider, volatility, sort]);
  const isFiltered = provider !== 'Alle' || volatility !== 'Alle';
  const showInline = showRealMoneyCtas && rankingLayout === 'list' && visible.length > 10;
  const reset = () => {
    setProvider('Alle');
    setVolatility('Alle');
    setSort('rank');
  };
  return <section id="top-30" aria-labelledby="ranking-title" className="mx-auto max-w-7xl scroll-mt-28 px-4 sm:px-6" data-id="element-172" data-mp="6oa4wsr21af3">
      <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between" data-id="element-173" data-mp="lfampodoygnw">
        <div data-id="element-174" data-mp="ggg68t37546y">
          <h2 id="ranking-title" className="font-serif text-3xl font-semibold tracking-tight sm:text-4xl" data-id="element-175" data-mp="ggpnnsa6e311">
            De volledige Top 30
          </h2>
          <p className="mt-2 max-w-xl text-muted" data-id="element-176" data-mp="3jwdhz6h0ak7e">
            Gerangschikt op SlotGids Score. Probeer elke slot gratis in de demo voordat je met echt geld speelt.
          </p>
        </div>
        <p className="text-sm text-muted" aria-live="polite" data-id="element-177" data-mp="3ts84wds9j1fz">
          {visible.length} van 30 slots
        </p>
      </div>

      <div className="mt-6 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between" data-id="element-178" data-mp="1e0aicvw493s0">
        <div role="group" aria-label="Filter op provider" className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1 sm:mx-0 sm:px-0" data-id="element-179" data-mp="192iuqe2i83tm">
          {providerFilters.map(p => {
          const active = provider === p;
          return <button key={p} type="button" onClick={() => {
            setProvider(p);
            trackEvent('ranking_filter', {
              filter_type: 'provider',
              value: p
            });
          }} aria-pressed={active} className={`h-9 shrink-0 whitespace-nowrap rounded-full border px-4 text-sm font-medium transition-colors duration-150 ${active ? 'border-ink bg-ink text-white' : 'border-line bg-surface text-ink hover:border-ink/30'}`} data-id="element-180" data-mp="17lv9zq5uqbg8">
                {p}
              </button>;
        })}
        </div>
        <div className="flex gap-2" data-id="element-181" data-mp="2iacc3yyl9gon">
          <label className="sr-only" htmlFor="vol-filter" data-id="element-182" data-mp="2n2lxd1ordskl">Volatiliteit</label>
          <select id="vol-filter" value={volatility} onChange={e => {
          setVolatility(e.target.value as 'Alle' | Volatility);
          trackEvent('ranking_filter', {
            filter_type: 'volatility',
            value: e.target.value
          });
        }} className="h-9 flex-1 rounded-lg border border-line bg-surface px-3 text-sm lg:flex-none" data-id="element-183" data-mp="2n4q8o4ozltuw">
            {volatilityFilters.map(v => <option key={v} value={v} data-id="element-184" data-mp="1i51rnpgbp2ea">
                {v === 'Alle' ? 'Alle volatiliteit' : `Volatiliteit: ${v}`}
              </option>)}
          </select>
          <label className="sr-only" htmlFor="sort" data-id="element-185" data-mp="18eyynp97bofy">Sorteren</label>
          <select id="sort" value={sort} onChange={e => {
          setSort(e.target.value as SortKey);
          trackEvent('ranking_filter', {
            filter_type: 'sort',
            value: e.target.value
          });
        }} className="h-9 flex-1 rounded-lg border border-line bg-surface px-3 text-sm lg:flex-none" data-id="element-186" data-mp="18d5tkee8lca2">
            <option value="rank" data-id="element-187" data-mp="3escnkzuoex2z">Sorteer: SlotGids rang</option>
            <option value="rtp" data-id="element-188" data-mp="3jq4fhtvbmj76">Sorteer: hoogste RTP</option>
            <option value="maxwin" data-id="element-189" data-mp="3jqnzdg4j7hnu">Sorteer: hoogste max. winst</option>
            <option value="new" data-id="element-190" data-mp="3nqy1uit47pxy">Sorteer: nieuwste</option>
          </select>
        </div>
      </div>

      {visible.length === 0 ? <div className="mt-6 flex flex-col items-center rounded-2xl border border-dashed border-line bg-surface px-6 py-16 text-center" data-id="element-191" data-mp="3sozlp5yd7tah">
          <SearchXIcon className="h-8 w-8 text-muted" aria-hidden="true" data-id="element-192" data-mp="6jm55t1u7ynt" />
          <p className="mt-3 font-semibold" data-id="element-193" data-mp="6hj9othx1dbp">Geen slots met deze combinatie</p>
          <p className="mt-1 text-sm text-muted" data-id="element-194" data-mp="bbdz8dk7cmip">Pas de filters aan of bekijk de volledige Top 30.</p>
          <button type="button" onClick={reset} className="mt-4 text-sm font-semibold text-fern underline underline-offset-2" data-id="element-195" data-mp="fbec3trnkpnn">
            Filters wissen
          </button>
        </div> : rankingLayout === 'grid' ? <ul className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-196" data-mp="2ldi3m3lgpt92">
          {visible.map(s => <SlotCard key={s.slug} slot={s} onDemo={onDemo} data-id="element-197" data-mp="2gep0n53sxkn5" />)}
        </ul> : <div className="mt-6 overflow-hidden rounded-2xl border border-line bg-surface" data-id="element-198" data-mp="2v3bh5a560ify">
          <div className={`hidden gap-x-4 border-b border-line bg-paper/60 px-6 py-3 text-xs font-medium text-muted md:grid ${rowGrid}`} aria-hidden="true" data-id="element-199" data-mp="2qcl9trd6i5im">
            <span data-id="element-200" data-mp="345uh9mhkggyi">#</span>
            <span data-id="element-201" data-mp="302xs1cxitmll">Slot</span>
            <span data-id="element-202" data-mp="301mnvc05ycty">RTP</span>
            <span data-id="element-203" data-mp="304h8p4n39znw">Volatiliteit</span>
            <span data-id="element-204" data-mp="3e1xjescf4flg">Max. winst</span>
            <span data-id="element-205" data-mp="3e0us4jb6ob50">Score</span>
            <span className="text-right" data-id="element-206" data-mp="3e034buntvhpa">{showRealMoneyCtas ? 'Spelen' : 'Bekijken'}</span>
          </div>
          <ol data-id="element-207" data-mp="3919s05ka1wqp">
            {visible.map((s, i) => <Fragment key={s.slug} data-id="element-208" data-mp="1jp7p49i514dj" data-mpi={i}>
                <SlotRow slot={s} onDemo={onDemo} data-id="element-209" data-mp="1jq90yg6z9uwb" />
                {showInline && i === 9 && <li data-id="element-210" data-mp="1er66fpomj72l">
                    <CasinoInlineOffer data-id="element-211" data-mp="3qcos757au6td" />
                  </li>}
              </Fragment>)}
          </ol>
        </div>}

      {isFiltered && visible.length > 0 && <button type="button" onClick={reset} className="mt-4 text-sm font-semibold text-fern underline underline-offset-2" data-id="element-212" data-mp="3gmvf1zridjn0">
          Toon de volledige Top 30
        </button>}
    </section>;
}